import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { translateMenuTerm } from '@/i18n/translateMenuContent';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/contexts/CartContext';
import { onDemoProductImageError } from '@/lib/demo/demoBusiness';
import { pickCrossSellProducts } from '@/lib/upsell/pickCrossSellProducts';
import { deriveProductSemanticTags } from '@/lib/menuSemanticTags';
import {
  productIncludedHasDrink,
  productIncludedHasFries,
  productIsFriesSideCandidate,
  productLooksLikeDrinkItem,
} from '@/lib/productComposition';
import { trackEvent } from '@/lib/analytics/trackEvent';

function buildMenuDataForCrossSell({ menuData, business, products, suggestedProductIds }) {
  const base = menuData
    ? {
        ...menuData,
        business: { ...(menuData.business || {}) },
        products:
          Array.isArray(menuData.products) && menuData.products.length > 0
            ? menuData.products
            : products || [],
        promos: Array.isArray(menuData.promos) ? menuData.promos : [],
      }
    : { business: { ...(business || {}) }, products: products || [], promos: [] };
  const b = { ...(base.business || {}) };
  const cur = b.suggested_product_ids;
  if ((!cur || !cur.length) && Array.isArray(suggestedProductIds) && suggestedProductIds.length) {
    b.suggested_product_ids = [...suggestedProductIds];
  }
  return { ...base, business: b };
}

function crossSellSubcopy(product, suggestions, menuDataBuilt, t) {
  if (!product || !suggestions?.length) {
    return t('catalog.crossSellDefault');
  }
  const promos = (menuDataBuilt?.promos || []).filter((p) => p.is_active !== false);
  const addedTags = deriveProductSemanticTags(product, menuDataBuilt?.business, promos);
  const addedHasFries = productIncludedHasFries(product) || addedTags.has_fries;
  const addedHasDrink = productIncludedHasDrink(product) || addedTags.has_drink;
  const anyDrinkSuggestion = suggestions.some((p) => productLooksLikeDrinkItem(p));
  const anySideSuggestion = suggestions.some((p) => productIsFriesSideCandidate(p));
  if (!addedHasDrink && anyDrinkSuggestion) return t('catalog.crossSellDrink');
  if (!addedHasFries && anySideSuggestion) return t('catalog.crossSellSide');
  if (anyDrinkSuggestion) return t('catalog.crossSellDrink');
  if (anySideSuggestion) return t('catalog.crossSellSide');
  return t('catalog.crossSellDefault');
}

const ComplementSuggestions = ({
  product,
  products,
  businessId,
  /** Negocio del menú (para fallback de imágenes en demo). */
  business = null,
  /** Menú completo (promos + negocio) para heurística alineada al asistente. */
  menuData = null,
  /** Líneas del carrito; si no se pasa, se usa el carrito del contexto. */
  cartLines = undefined,
  isOpen,
  onClose,
  onAddComplement,
  onGoToCart,
  onPayNow,
  suggestedProductIds = [],
}) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const { addToCart, cart } = useCart();
  const [suggestions, setSuggestions] = useState([]);
  const shownKeyRef = useRef('');

  const menuDataBuilt = useMemo(
    () => buildMenuDataForCrossSell({ menuData, business, products, suggestedProductIds }),
    [menuData, business, products, suggestedProductIds]
  );

  const subcopy = useMemo(
    () => crossSellSubcopy(product, suggestions, menuDataBuilt, t),
    [product, suggestions, menuDataBuilt, t]
  );

  useEffect(() => {
    if (!isOpen || !product) {
      setSuggestions([]);
      shownKeyRef.current = '';
      return;
    }
    const lines = cartLines !== undefined && cartLines !== null ? cartLines : cart;
    const list = pickCrossSellProducts(product, {
      menuData: menuDataBuilt,
      cartLines: lines,
      limit: 3,
    });
    setSuggestions(list);
    if (!list.length) {
      onClose?.();
      return;
    }
    if (businessId) {
      const key = `${product.id}:${list.map((p) => p.id).join(',')}`;
      if (shownKeyRef.current !== key) {
        shownKeyRef.current = key;
        trackEvent('cross_sell_shown', {
          business_id: businessId,
          product_id: product.id,
          suggestion_ids: list.map((p) => p.id),
        });
      }
    }
  }, [isOpen, product, menuDataBuilt, cartLines, cart, businessId, onClose]);

  const handleAddComplement = (complementProduct) => {
    if (!businessId || !product) return;
    trackEvent('cross_sell_added', {
      business_id: businessId,
      product_id: product.id,
      added_product_id: complementProduct.id,
    });
    addToCart(complementProduct, [], [], 1, { businessId });
    toast.success(t('catalog.addedToCart', { name: complementProduct.name }));
    if (onAddComplement) {
      onAddComplement(complementProduct);
    }
  };

  const handleSkip = () => {
    if (businessId && product) {
      trackEvent('cross_sell_skipped', {
        business_id: businessId,
        product_id: product.id,
      });
    }
    onClose?.();
  };

  const handleGoToCart = () => {
    onClose?.();
    if (typeof onGoToCart === 'function') {
      onGoToCart();
    }
  };

  const handlePayNow = () => {
    onClose?.();
    if (typeof onPayNow === 'function') {
      onPayNow();
    }
  };

  if (!suggestions.length) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('catalog.crossSellTitle')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground">{subcopy}</p>

          <div className="grid gap-4">
            {suggestions.map((item) => (
              <Card key={item.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex gap-4">
                  {item.photo_url && (
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={item.photo_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => onDemoProductImageError(e, business)}
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg mb-1">{translateMenuTerm(item.name, lng)}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {translateMenuTerm(item.description, lng)}
                    </p>
                    <div className="flex items-center gap-2">
                      {item.discount_percentage > 0 ? (
                        <>
                          <span className="text-xl font-bold">
                            ${(item.final_price || item.price).toLocaleString()}
                          </span>
                          <span className="text-sm text-muted-foreground line-through">
                            ${item.price.toLocaleString()}
                          </span>
                          <Badge variant="destructive" className="text-xs">
                            -{item.discount_percentage}%
                          </Badge>
                        </>
                      ) : (
                        <span className="text-xl font-bold">${item.price.toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  <Button onClick={() => handleAddComplement(item)} className="h-10 shrink-0">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('catalog.add')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {typeof onPayNow === 'function' ? (
            <div className="grid gap-3 pt-4 border-t sm:grid-cols-3">
              <Button variant="outline" className="w-full" onClick={handleSkip}>
                {t('catalog.keepBrowsing')}
              </Button>
              <Button variant="outline" className="w-full" onClick={handleGoToCart}>
                {t('catalog.fullCart')}
              </Button>
              <Button className="w-full" onClick={handlePayNow}>
                {t('catalog.payNow')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={handleSkip}>
                {t('catalog.keepBrowsing')}
              </Button>
              <Button className="flex-1" onClick={handleGoToCart}>
                {t('catalog.goToCart')}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComplementSuggestions;
